import './Header.scss';

import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button/Button';
import { partial } from 'lodash';
import PropTypes from 'prop-types';

import NewTimecard from 'features/timesheets/TimesheetsPage/TimecardModal/EditMode/Header/NewTimecard';
import RoleSelectInput from 'features/timesheets/TimesheetsPage/TimecardModal/EditMode/Header/RoleSelectInput';
import { getShiftMetadata } from 'features/timesheets/TimesheetsPage/TimecardModal/utils.js';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import { trackUxEvent } from 'util/tracking';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';

import DeleteTimecardModal from '../../DeleteTimecardModal';
const { cx, cxEl } = cxHelpers('HeaderEditMode');

const Header = ({
  date,
  employeeName,
  locationName,
  onChangeShift,
  onChangeTimecardDate,
  newTimecard,
  editableShift,
  employeeOptions,
  addLocationRole,
  roleOptions,
  isMobile,
  deleteTimecard,
  hasTimecard,
  setChangesMade,
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const handleClose = useCallback(() => setOpen(false), []);

  const handleConfirmDelete = useCallback(() => {
    setOpen(false);
    dispatch(() =>
      deleteTimecard({
        id: editableShift.get('id'),
        originPath: window.location.pathname,
      })
    );
  }, [dispatch, deleteTimecard, editableShift]);

  const handleDelete = useCallback(() => {
    trackUxEvent({
      actionType: TRACK_ACTION_TYPES.CLICK,
      productArea: PRODUCT_AREAS.TIMESHEETS,
      eventCategory: EVENT_CATEGORIES.TIMECARD_DETAIL,
      eventAction: EVENT_ACTIONS.DELETE_TIMECARD_CLICKED_V3,
      properties: getShiftMetadata(editableShift),
    });

    setOpen(!open);
  }, [open, editableShift]);

  if (newTimecard) {
    return (
      <NewTimecard
        employeeJobId={editableShift.get('job_id')}
        setEmployeeJobId={partial(onChangeShift, 'job_id')}
        roleId={editableShift.get('role_id')}
        setRoleId={partial(onChangeShift, 'role_id')}
        timecardDate={date.format('YYYY-MM-DD')}
        setTimecardDate={onChangeTimecardDate}
        employeeOptions={employeeOptions}
        addLocationRole={addLocationRole}
        roleOptions={roleOptions}
        locationName={locationName}
        isMobile={isMobile}
        setChangesMade={setChangesMade}
      />
    );
  }

  return (
    <Box className={cx()}>
      {open && (
        <DeleteTimecardModal
          open={open}
          handleConfirmDelete={handleConfirmDelete}
          handleClose={handleClose}
        />
      )}
      <Box>
        <Box row>
          <Box minw={0} grow={1}>
            <Box>
              <Text variant="eyebrow" color="mono700">
                {date.format('ddd, MMM D')}
              </Text>
            </Box>
            <Box mt={5} ellipsis maxw="70%">
              <Text variant="heading2" color="mono900">
                {employeeName}
              </Text>
            </Box>
            <Box mt={5}>
              <RoleSelectInput
                roleId={editableShift.get('role_id') || undefined}
                roleOptions={roleOptions}
                setRoleId={partial(onChangeShift, 'role_id')}
                locationName={locationName}
                addLocationRole={addLocationRole}
                setChangesMade={setChangesMade}
              />
            </Box>
          </Box>
          {hasTimecard && (
            <Box pr={isMobile ? 8 : 0}>
              <Button
                className={cxEl('deleteButton')}
                onClick={handleDelete}
                size="medium"
                variant="secondaryDestructive"
                startIcon="Delete"
              >
                {toI18n('timecard_modal_v3.header.delete.title')}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

Header.propTypes = {
  date: PropTypes.object.isRequired,
  roleName: PropTypes.string,
  locationName: PropTypes.string,
  employeeName: PropTypes.string,
  setChangesMade: PropTypes.func.isRequired,
};

export default Header;
